<script setup>
import StylishCheck from "@/assets/icons/StylishCheck.vue";
import { goalOptions } from "@/services/constant-services";
import { useUserStore } from "@/store/user-store";
import { ref, watchEffect } from "vue";

const props = defineProps({
  goals: {
    type: Array,
    default: [],
  },
});

const store = useUserStore();

const dob = store.dashboardData?.dob;
const goalList = ref(null);
const tempGoals = [
  {
    goalName: "Today",
    goalDueDate: new Date(),
  },
  {
    goalName: "eg. Start_Business",
    goalDueDate: new Date().setFullYear(new Date().getFullYear() + 5),
  },
  {
    goalName: "eg. Retirement",
    goalDueDate: new Date().setFullYear(new Date().getFullYear() + 10),
  },
  {
    goalName: "Assessed Life",
    goalDueDate: new Date().setFullYear(new Date().getFullYear() + 15),
  },
];

const getIcon = (name) => {
  const matchLabel =
    props.goals?.length < 1 ? name.split(" ")[1].replace("_", " ") : name;
  const icon = goalOptions.find((item) => item.label === matchLabel);
  return icon?.icon;
};

watchEffect(() => {
  const temp = [
    {
      goalName: "Today",
      goalDueDate: new Date(),
    },
    ...props.goals.sort(
      (a, b) => new Date(a.goalDueDate) - new Date(b.goalDueDate)
    ),
    {
      goalName: "Assessed Life",
      goalDueDate: `${new Date(dob).getFullYear() + 85}-01-01`,
    },
  ];
  goalList.value = props.goals.length < 1 ? tempGoals : temp;
});
</script>

<template>
  <div class="progress-wrapper d-flex w-100 align-items-center">
    <template v-for="(item, index) in goalList">
      <div class="d-flex flex-direction-column position-relative">
        <div class="position-relative">
          <span
            class="text-color position-absolute font-weight-500"
            :style="{
              left:
                index === 0
                  ? '11px'
                  : index !== goalList.length - 1
                  ? '50%'
                  : null,
              right: index === goalList.length - 1 && 0,
              top: '-30px',
              transform:
                index !== goalList.length - 1 &&
                index !== 0 &&
                'translateX(-50%)',
            }"
            >{{ item.goalName }}</span
          >
        </div>
        <div
          class="icon-container"
          :style="{
            backgroundColor: goals.length < 1 && 'var(--an-gray)',
            borderColor: goals.length < 1 && 'var(--an-gray1)',
          }"
        >
          <component
            :is="
              item.goalName === 'Today' ? StylishCheck : getIcon(item.goalName)
            "
            :color="goals.length < 1 ? 'var(--an-gray1)' : 'var(--an-white)'"
          />
        </div>
        <div class="position-relative d-flex justify-center">
          <span
            :class="[
              'text-color position-absolute font-weight-500',
              { 'l-0': index === 0 },
              { 'r-0': index === goalList.length - 1 },
            ]"
            :style="{
              color: goals.length < 1 && 'var(--an-gray)',
            }"
            >{{
              new Date(item.goalDueDate)
                .toLocaleDateString("en-GB", {
                  month: "short",
                  year: "numeric",
                })
                .replace(" ", ", ")
            }}</span
          >
        </div>
      </div>
      <div
        class="progress-bar flex-1 d-flex align-items-center position-relative"
        v-if="index < goalList.length - 1"
      >
        <div
          class="before-curve"
          :style="{
            '--an-pseudo':
              goals.length < 1
                ? 'var(--an-gray1)'
                : 'var(--an-background-light)',
          }"
        />
        <div
          class="progress-line1 position-absolute"
          :style="{
            backgroundColor: goals.length < 1 && 'var(--an-gray1)',
          }"
        />
        <div
          :style="{
            width: item.progress ? `calc(${item.progress}% + 19px)` : 0,
          }"
          class="progress-line2 position-absolute"
        />
        <div
          class="after-curve"
          :style="{
            '--an-pseudo':
              goals.length < 1
                ? 'var(--an-gray1)'
                : 'var(--an-background-light)',
          }"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.icon-container {
  position: relative;
  width: 70px;
  height: 70px;
  background-color: var(--an-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 8px solid var(--an-background-light);
}

.progress-line1 {
  height: 12px;
  width: 102%;
  left: -1px;
  background-color: var(--an-background-light);
}

.progress-line2 {
  height: 5px;
  background-color: var(--an-primary);
  left: -10px;
  z-index: 1;
}

.before-curve::before {
  --_size: 30px;
  content: "";
  position: absolute;
  width: var(--_size);
  aspect-ratio: 1;
  background-image: radial-gradient(
    circle at 100% 82%,
    transparent var(--_size),
    var(--an-pseudo) calc(var(--_size) + 1px)
  );
  top: -36px;
  left: -5px;
  rotate: -90deg;
}

.before-curve::after {
  --_size: 30px;
  content: "";
  position: absolute;
  width: var(--_size);
  aspect-ratio: 1;
  background-image: radial-gradient(
    circle at 82% 100%,
    transparent var(--_size),
    var(--an-pseudo) calc(var(--_size) + 1px)
  );
  top: 6px;
  left: -5px;
}

.after-curve::before {
  --_size: 30px;
  content: "";
  position: absolute;
  width: var(--_size);
  aspect-ratio: 1;
  background-image: radial-gradient(
    circle at 82% 100%,
    transparent var(--_size),
    var(--an-pseudo) calc(var(--_size) + 1px)
  );
  top: -36px;
  right: -5px;
  rotate: -180deg;
}

.after-curve::after {
  --_size: 30px;
  content: "";
  position: absolute;
  width: var(--_size);
  aspect-ratio: 1;
  background-image: radial-gradient(
    circle at 100% 82%,
    transparent var(--_size),
    var(--an-pseudo) calc(var(--_size) + 1px)
  );
  top: 6px;
  right: -5px;
  rotate: 90deg;
}

.text-color {
  white-space: nowrap;
}
</style>
